import { TurnoverTableInnerProps } from '../../models/table-models';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '../../components/CustomTable';
import { turnoverTableHeaders } from '../../utils/Enums/TableHeaders';
import dateFormat from 'dateformat';
import { date } from '../../utils/Enums/Common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faTools } from '@fortawesome/free-solid-svg-icons';
import { PaymentMethodIcon } from '../../components/PaymentMethodIcon';
import { Role } from '../../utils/Enums/Roles';
import { PaginationComponent } from '../../components/Pagination';
import { NoDataComponent } from '../../components/NoDataComponent';
import React from 'react';
import { deleteTurnover, useGetInvoices, useGetDocument } from "../../utils/clients-hooks";
import { Turnover } from "../../models/user-models";
import { resourceCache } from "use-async-resource";
import { useGetAllTurnovers, useGetAllDocuments } from "../../utils/users-hooks";
import { toast } from "react-toastify";
import { CustomToastContent } from "../../components/CustomToastContent";
import { toastOptionsTopRight } from "../../utils/Enums/ToastOptions";
import { getLetter, getCurrencySymbol } from "../../utils/helper-functions";

export const TurnoversInner = ({
    items,
    users,
    pagination,
    refetchEntitiesHook,
    pager,
    setSelectedItem,
}: TurnoverTableInnerProps) => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'invoice' });
    const money = (value: number) => value.toLocaleString(i18n.resolvedLanguage || i18n.language, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: openingT } = useTranslation('translation', { keyPrefix: 'openingBalances' });

    const useDeleteTurnover = async (turnover: Turnover) => {
        if (!turnover.id || turnover.closedInvoicePayment) return;
        if (turnover.invoice.openingBalanceId) {
            toast.info(openingT('turnoverHint'));
            return;
        }
        await deleteTurnover(turnover);
        resourceCache(useGetAllTurnovers).clear();
        resourceCache(useGetInvoices).clear();
        resourceCache(useGetAllDocuments).clear();
        resourceCache(useGetDocument).clear();
        toast.success(<CustomToastContent content={[t('turnoverDeleted')]} />, toastOptionsTopRight);
    };

    const transformedItems = items.map(item => {
        const cashFormatted = `${money(item.cash / 100)}${getCurrencySymbol(item.invoice.currency)}`;
        const paid = item.invoice.paidAmount ?? 0;
        const showSettlement = !item.unpaidBank && item.cash > 0 && !item.invoice.openingBalanceId
            && item.paymentMethod === 'BANK' && paid > 0 && paid < item.invoice.total;
        return {
            ...item,
            // Table data is recursively summed: keep React elements exclusively in cell renderers.
            cashFormatted,
            showSettlement,
            cash: Number((item.cash / 100).toFixed(2))
        };
    });

    const renderSettlement = (invoice: Turnover['invoice'], cashFormatted: string) => {
        const paid = invoice.paidAmount ?? 0;
        return <div>
                <span className='text-nowrap'>{cashFormatted}</span>
                <small className='d-block text-nowrap'>{openingT('invoiceAmount', {
                    amount: `${money(invoice.total / 100)}${getCurrencySymbol(invoice.currency)}`,
                })}</small>
                <small className='d-block text-nowrap'>{openingT('settledAmount', {
                    amount: `${money(paid / 100)}${getCurrencySymbol(invoice.currency)}`,
                })}</small>
                <small className='d-block text-danger text-nowrap'>{openingT('remainingAmount', {
                    amount: `${money((invoice.total - paid) / 100)}${getCurrencySymbol(invoice.currency)}`,
                })}</small>
            </div>;
    };

    return (
        <>
            {items.length > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: turnoverTableHeaders,
                            items: transformedItems.map(({ invoice, cash, cashFormatted, showSettlement, userId, paymentMethod, taskType, unpaidBank }) => [
                                invoice.documentOnly ? t(invoice.document?.documentFailType?.toLowerCase() ?? '')
                                    : invoice.openingBalanceId ? openingT('turnoverSource') : getLetter(cash, invoice.document?.documentType),
                                (cash < 0 ? invoice.document?.creditNoteNumber || "" : invoice.document?.invoiceNumber || invoice.invoiceNumber || ""),
                                invoice.document?.documentType.toLowerCase() == "receipt" ? "" : invoice.branchName,
                                invoice.document?.documentType.toLowerCase() == "receipt" ? t("privatePerson") : invoice.document?.clientName ?? invoice.companyName,
                                invoice.document?.documentType.toLowerCase() == "receipt" ? "" : invoice.document?.eik ?? invoice.eik,
                                invoice.documentOnly
                                    ? (invoice.responsibleUserIds ?? []).map(id => users.find(user => user.id === id)?.name ?? String(id)).join(', ') || '—'
                                    : users.find((user) => user.id === userId)?.name ?? '—',
                                users.find((user) => user.id === (invoice.recordedBy ?? invoice.document?.userId))?.name ?? '—',
                                invoice.deviceCount,
                                invoice.fromDate ? dateFormat(invoice.fromDate, date) : '',
                                invoice.duePaymentDate ? dateFormat(invoice.duePaymentDate, date) : '',
                                // Датата на самата фактура, не на задължението: номерът - а с него и
                                // датата - се дава по-късно от създаването на реда, и двете се
                                // разминават. Резервният вариант е за ред без издаден документ.
                                invoice.openingBalanceId
                                    ? <span>{dateFormat(unpaidBank ? invoice.openingAsOfDate : invoice.receiptDate, date)}<small className='d-block'>{openingT('receiptInvoiceDate', { date: dateFormat(invoice.creationDate, date) })}</small></span>
                                    : dateFormat(invoice.document?.invoiceDate ?? invoice.creationDate, date),
                                // Неплатеният ред показва дължимото, не постъпило - оттам и червеният
                                // надпис до сумата: иначе на екрана изглежда като взети пари.
                                unpaidBank
                                    ? <span className='text-danger'>{cashFormatted} <small>({t('unpaid')})</small></span>
                                    : showSettlement ? renderSettlement(invoice, cashFormatted) : cashFormatted,
                                <div className='d-flex justify-content-center align-items-center'>
                                    <PaymentMethodIcon {...{ paymentMethod }} />
                                </div>,
                                (taskType ? <div className='d-flex justify-content-center align-items-center'>
                                    <FontAwesomeIcon
                                        title={t(taskType.toLowerCase())}
                                        size='lg'
                                        icon={taskType.toLowerCase() === 'installation' ? faCartPlus : faTools}
                                    />
                                </div> : ""),
                            ]),
                        }}
                        actionHook={useDeleteTurnover}
                        actionTextForRow={row => openingT('deleteReceiptConfirmation', {
                            amount: `${money(row.cash)} ${getCurrencySymbol(row.invoice.currency)}`,
                            number: row.invoice.document?.invoiceNumber ?? row.invoice.invoiceNumber ?? '',
                        })}
                        disableAction={row => !row.id || !!row.invoice.openingBalanceId || !!row.closedInvoicePayment}
                        disableRowClick={row => !!row.invoice.documentOnly || !!row.invoice.document?.documentFailType}
                        actionDisabledReason={row => row.invoice.openingBalanceId ? openingT('deleteDisabled')
                            : row.closedInvoicePayment ? openingT('closedPaymentDeleteDisabled') : undefined}
                        viewComponentObject={(row) => setSelectedItem(
                            // Редът се подава цял, а не по id: неплатената банкова фактура няма запис в
                            // оборота, тоест id-то ѝ е празно и търсенето по него не намираше нищо -
                            // диалогът с подробности просто не се отваряше. Връща се оригиналният ред,
                            // защото показваният носи сумата в левове, а не в стотинки.
                            items.find((value) => value.id === row.id && value.invoice.id === row.invoice.id))}
                        accessEditRoles={[Role.FINANCIAL]}
                        accessActionRoles={[Role.ADMIN]}
                        tableData={transformedItems}
                        {...{ refetchEntitiesHook }}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                </>
            ) : (
                <NoDataComponent noDataText={commonT('payments')}></NoDataComponent>
            )}
        </>
    );
};
